import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';

import ConfirmDialog, { ConfirmDialogResponse } from '../../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../../atoms/Dialog/MessageDialog';
import { SettingsState } from '../../../organisms/SelectSettings';

import { useModal } from '../../../../function/useModal';

import StorageCapacityFooter from './StorageCapacitySettingFooter';
import {
  FILE_STORAGE_PERIOD,
  GetStorageCapacitySetting,
  CreateFILE_STORAGE_PERIOD,
  PutStorageCapacitySetting,
  CheckStorageCapacitySetting,
} from './StorageCapacitySetting';
import StorageCapacityEditBody from './StorageCapacitySettingEditBody';

type StorageCapacitySettingPageProps = { editData: SettingsState };

/**
 *ストレージ容量設定
 * @returns
 */
const StorageCapacitySettingPage: React.FC<StorageCapacitySettingPageProps> = (props) => {
  const [storageSetting, setStorageSetting] = useState<FILE_STORAGE_PERIOD>(CreateFILE_STORAGE_PERIOD());

  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();

  const [dlgmsg, setDlgmsg] = useState<string>('');
  const [buttonEnable, setButtonEnable] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('StorageCapacitySettingPage.はい', 'はい')}
      cancel={t('StorageCapacitySettingPage.いいえ', 'いいえ')}
    />
  ) : null;

  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;

  useEffect(() => {
    console.debug(['useEffect props.editData.CancelCondition', props]);
    props.editData.SaveFunction = saveAction2;
    props.editData.IsUpdate = false;
  }, [props.editData.CancelCondition]);
  /**
   * 初期表示　useEffect
   */
  useEffect(() => {
    console.debug(['useEffect init', props]);
    upDatePage();
  }, []);

  /**
   * 画面更新
   */
  const upDatePage = () => {
    setButtonEnable(false);
    GetStorageCapacitySetting()
      .then((r: FILE_STORAGE_PERIOD) => {
        console.debug([' upDatePage - OK ', r]);
        setStorageSetting(r);
      })
      .catch((e) => {
        setDlgmsg(
          t('StorageCapacitySettingPage.データ取得中にエラーが発生しました', 'データ取得中にエラーが発生しました')
        );
        actMsgDlg.open();
        console.error(['upDatePage - ERROR', e]);
      })
      .finally(() => {
        setButtonEnable(true);
      });
  };

  /**
   *保存ボタン
   * @param index
   */
  const saveAction = () => {
    console.debug(['saveAction', storageSetting]);
    if (CheckStorageCapacitySetting(storageSetting)) {
      setDlgmsg(
        t(
          'StorageCapacitySettingPage.編集内容を保存します。\nよろしいですか?',
          '編集内容を保存します。\nよろしいですか?'
        )
      );
      actCfmDlg
        .open()
        .apply((_) => {
          setButtonEnable(false);
          PutStorageCapacitySetting(storageSetting)
            .then((r) => {
              setDlgmsg(t('StorageCapacitySettingPage.保存が正常に終了しました', '保存が正常に終了しました'));
              actMsgDlg.open();
              upDatePage(); //再描画
              props.editData.IsUpdate = false;
              console.debug('saveAction ok');
            })
            .catch((r) => {
              setDlgmsg(t('StorageCapacitySettingPage.保存中にエラーが発生しました', '保存中にエラーが発生しました'));
              actMsgDlg.open();
              console.error(['saveAction error', r]);
            })
            .finally(() => {
              setButtonEnable(true);
            });
        })
        .cancel(() => {
          console.debug('saveAction cancel');
        });
    } else {
      console.debug(['saveAction no edit', storageSetting]);
    }
  };

  /**
   * タブ切替時に動作
   * @param param
   * @returns
   */
  const saveAction2 = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as FILE_STORAGE_PERIOD;
      console.debug(['★saveAction2', param]);
      setButtonEnable(false);
      PutStorageCapacitySetting(sp)
        .then((r) => {
          props.editData.IsUpdate = false;
          console.debug('saveAction ok');
          resolve('done');
        })
        .catch((r) => {
          setDlgmsg(t('StorageCapacitySettingPage.保存中にエラーが発生しました', '保存中にエラーが発生しました'));
          actMsgDlg.open();
          console.error(['saveAction error', r]);
          reject('data error');
        })
        .finally(() => {
          setButtonEnable(true);
        });
    });
  };

  /**
   *キャンセルボタン押下
   */
  const cancelAction = () => {
    console.debug(['cancelAction', storageSetting]);
    if (CheckStorageCapacitySetting(storageSetting)) {
      setDlgmsg(
        t(
          'StorageCapacitySettingPage.保存していないデータを破棄します。\nよろしいですか?',
          '保存していないデータを破棄します。\nよろしいですか?'
        )
      );
      actCfmDlg
        .open()
        .apply((_) => {
          upDatePage(); //再描画
          props.editData.IsUpdate = false;
          console.debug('cancelAction ok');
        })
        .cancel(() => {
          console.debug('cancelAction cancel');
        });
    } else {
      console.debug(['cancelAction - no edit', storageSetting]);
    }
  };

  /**
   *ストレージ設定変更通知
   * @returns
   */
  const changeActionStorageSetting = (target: FILE_STORAGE_PERIOD) => {
    console.debug(['changeActionStorageSetting - start', storageSetting, target]);
    target.isEdit = true;
    storageSetting.FILE_RET_PERIOD = target.FILE_RET_PERIOD;
    storageSetting.SAV_INDEX = target.SAV_INDEX;
    storageSetting.FREE_SPACE = target.FREE_SPACE;
    storageSetting.isEdit = target.isEdit;
    props.editData.Target = storageSetting;
    props.editData.IsUpdate = true;
    console.debug(['changeActionStorageSetting - end', storageSetting, target]);
  };

  return (
    <>
      <Grid container justify="flex-start" spacing={1} direction="row" alignItems="center" style={{ margin: 5 }}>
        <Grid item xs={12}>
          <StorageCapacityEditBody
            storageSetting={storageSetting}
            onChangeData={changeActionStorageSetting}
          ></StorageCapacityEditBody>
        </Grid>
        <Grid item xs={12}>
          <StorageCapacityFooter
            onClickSaveButton={saveAction}
            onClickCancelButton={cancelAction}
            buttonEnable={buttonEnable}
          ></StorageCapacityFooter>
        </Grid>
        {cfmdlg}
        {msgdlg}
      </Grid>
    </>
  );
};

export default StorageCapacitySettingPage;
